import { useEffect, useState } from "react";
import { amountTextToCents, centsToAmountText } from "../../utils/money";

interface MoneyInputProps {
  /** Amount in cents, the unit the API stores. */
  value: number;
  onChange: (cents: number) => void;
  currencySymbol?: string;
  id?: string;
  invalid?: boolean;
  placeholder?: string;
}

/**
 * Lets people type a normal amount ("5.00") while the form and API keep working in cents.
 * The typed text is kept separately so partial input such as "5." is never rewritten mid-typing.
 */
export function MoneyInput({ value, onChange, currencySymbol, id, invalid, placeholder = "0.00" }: MoneyInputProps) {
  const [text, setText] = useState(() => centsToAmountText(value));

  // Follow outside changes (loading a saved config, reset) without fighting the user's typing.
  useEffect(() => {
    if (amountTextToCents(text) !== value) {
      setText(centsToAmountText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="money-input" style={{ position: "relative" }}>
      {currencySymbol ? (
        <span
          aria-hidden="true"
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", opacity: 0.6, pointerEvents: "none" }}
        >
          {currencySymbol}
        </span>
      ) : null}
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        aria-invalid={invalid ? true : undefined}
        style={currencySymbol ? { paddingLeft: 12 + currencySymbol.length * 9 + 8 } : undefined}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          const cents = amountTextToCents(next);
          // An unreadable or empty entry becomes 0, which the form's "at least 0.01" rule reports.
          onChange(cents ?? 0);
        }}
        onBlur={() => {
          const cents = amountTextToCents(text);
          if (cents !== null) setText(centsToAmountText(cents));
        }}
      />
    </div>
  );
}

export default MoneyInput;
